import React, { useState, useEffect } from 'react';
import { useCareerCards } from '../../hooks/useCareerCards';
import { useFocusContent } from '../../hooks/useFocusContent';
import { useToast } from '../../context/ToastContext';
import { useDarkMode } from '../../context/DarkModeContext';
import { format } from 'date-fns';
import { XMarkIcon, CheckIcon, LinkIcon } from '@heroicons/react/24/outline';
import { CareerCard } from '../../types/careerCard';

interface RetroactiveLinkingModalProps {
  isOpen: boolean;
  onClose: () => void;
  focusId: string;
  focusType: string;
  startDate: string;
  endDate?: string;
}

const RetroactiveLinkingModal: React.FC<RetroactiveLinkingModalProps> = ({
  isOpen,
  onClose,
  focusId,
  focusType,
  startDate,
  endDate
}) => {
  const { darkMode } = useDarkMode();
  const { showToast } = useToast();
  const { cards, loading: cardsLoading } = useCareerCards();
  const { linkContentBulk } = useFocusContent(focusId);
  
  const [selectedCards, setSelectedCards] = useState<Set<string>>(new Set());
  const [isLinking, setIsLinking] = useState(false);
  const [filterType, setFilterType] = useState<'all' | 'date_range'>('date_range');
  
  // Filter cards based on date range if in date range mode
  const eligibleCards = cards?.filter(card => {
    if (filterType === 'all') return true;
    
    const cardDate = new Date(card.career_card_date);
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    
    return cardDate >= start && cardDate <= end;
  }) || [];
  
  // Group cards by week for better organization
  const groupedCards = eligibleCards.reduce((acc, card) => {
    const weekStart = format(new Date(card.career_card_date), 'yyyy-MM-dd');
    const week = format(new Date(card.career_card_date), 'MMM d, yyyy');
    
    if (!acc[week]) {
      acc[week] = [];
    }
    acc[week].push(card);
    return acc;
  }, {} as Record<string, CareerCard[]>);
  
  const handleToggleCard = (cardId: string) => {
    setSelectedCards(prev => {
      const next = new Set(prev);
      if (next.has(cardId)) {
        next.delete(cardId);
      } else {
        next.add(cardId);
      }
      return next;
    });
  };
  
  const handleSelectAll = () => {
    if (selectedCards.size === eligibleCards.length) {
      setSelectedCards(new Set());
    } else {
      setSelectedCards(new Set(eligibleCards.map(c => c.cardid)));
    }
  };
  
  const handleLinkCards = async () => {
    if (selectedCards.size === 0) {
      showToast('Please select at least one card to link', 'error');
      return;
    }
    
    setIsLinking(true);
    try {
      const cardIds = Array.from(selectedCards);
      const success = await linkContentBulk(cardIds, { linked_from: 'retroactive_setup' });
      
      if (success) {
        showToast(`Successfully linked ${cardIds.length} cards to your ${focusType}`, 'success');
        onClose();
      } else {
        showToast('Failed to link cards. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error linking cards:', error);
      showToast('An error occurred while linking cards', 'error');
    } finally {
      setIsLinking(false);
    }
  };
  
  if (!isOpen) return null;
  
  const bgColor = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textColor = darkMode ? 'text-gray-200' : 'text-gray-800';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      
      <div className={`relative w-full max-w-3xl max-h-[80vh] ${bgColor} rounded-lg shadow-xl flex flex-col`}>
        {/* Header */}
        <div className={`px-6 py-4 border-b ${borderColor}`}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className={`text-xl font-semibold ${textColor}`}>
                Link Previous Cards
              </h2>
              <p className={`text-sm ${mutedTextColor} mt-1`}>
                Select career cards from your {focusType} period to link them
              </p>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        {/* Filter Options */}
        <div className={`px-6 py-3 border-b ${borderColor}`}>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={filterType === 'date_range'}
                onChange={() => setFilterType('date_range')}
                className="text-blue-600"
              />
              <span className={`text-sm ${textColor}`}>
                Cards from {format(new Date(startDate), 'MMM d, yyyy')} to present
              </span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={filterType === 'all'}
                onChange={() => setFilterType('all')}
                className="text-blue-600"
              />
              <span className={`text-sm ${textColor}`}>All cards</span>
            </label>
          </div>
        </div>
        
        {/* Cards List */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {cardsLoading ? (
            <div className="animate-pulse space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className={`h-20 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded`} />
              ))}
            </div>
          ) : eligibleCards.length === 0 ? (
            <div className="text-center py-12">
              <LinkIcon className={`h-12 w-12 ${mutedTextColor} mx-auto mb-4`} />
              <p className={`${mutedTextColor}`}>
                No cards found for the selected period
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedCards).map(([week, weekCards]) => (
                <div key={week}>
                  <h3 className={`text-sm font-medium ${mutedTextColor} mb-2`}>
                    {week}
                  </h3>
                  <div className="space-y-2">
                    {weekCards.map(card => (
                      <label
                        key={card.cardid}
                        className={`
                          flex items-start gap-3 p-3 rounded-lg border cursor-pointer
                          ${selectedCards.has(card.cardid) 
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                            : borderColor
                          }
                          hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors
                        `}
                      >
                        <input
                          type="checkbox"
                          checked={selectedCards.has(card.cardid)}
                          onChange={() => handleToggleCard(card.cardid)}
                          className="mt-1 h-4 w-4 text-blue-600 rounded"
                        />
                        <div className="flex-1">
                          <p className={`font-medium ${textColor}`}>
                            {card.card_data?.title || 'Untitled'}
                          </p>
                          {card.card_data?.content && (
                            <p className={`text-sm ${mutedTextColor} mt-1 line-clamp-2`}>
                              {card.card_data.content}
                            </p>
                          )}
                          <p className={`text-xs ${mutedTextColor} mt-1`}>
                            {card.card_data?.type || 'Career Card'}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className={`px-6 py-4 border-t ${borderColor}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleSelectAll}
                className={`text-sm ${textColor} hover:text-blue-600 transition-colors`}
              >
                {selectedCards.size === eligibleCards.length ? 'Deselect All' : 'Select All'}
              </button>
              <span className={`text-sm ${mutedTextColor}`}>
                {selectedCards.size} of {eligibleCards.length} selected
              </span>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className={`px-4 py-2 text-sm ${textColor} hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors`}
              >
                Skip for Now
              </button>
              <button
                onClick={handleLinkCards}
                disabled={selectedCards.size === 0 || isLinking}
                className={`
                  px-4 py-2 text-sm bg-blue-600 text-white rounded-md
                  hover:bg-blue-700 transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed
                  flex items-center gap-2
                `}
              >
                {isLinking ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    Linking...
                  </>
                ) : (
                  <>
                    <CheckIcon className="h-4 w-4" />
                    Link {selectedCards.size} Cards
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetroactiveLinkingModal;